import React, { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { strings } from 'strings';

import { ErrorMessage, FlexContainer, Text } from '../../../../../../shared/Typhography';
import { Button, Input, SelectForm } from '../../../../../../shared/Components';
import { useRemoteConfig } from '../../../../../../context/RemoteConfigContextProvider';
import { GameEventField } from '../GameEventForm/validations/GameEventFormSchema';

import PuzzleItem from './components/PuzzleItem/PuzzleItem';
import AddNewItem from './components/AddNewItem/AddNewItem';

const PuzzleListContainer = styled(FlexContainer)`
  position: relative;
  flex-wrap: wrap;
`;

interface GameEventLevelFormProps {
  index: number;
  isLast: boolean;
  onRemove: (levelIndex: number) => void;
  level: number;
  disabled: boolean;
}

const GameEventLevelForm = ({
  index,
  isLast,
  onRemove,
  level,
  disabled,
}: GameEventLevelFormProps) => {
  const { control, formState, getValues, trigger } = useFormContext<GameEventField>();
  const { gameplay } = useRemoteConfig();

  const data = getValues();

  const {
    fields: puzzles,
    append,
    remove,
  } = useFieldArray<GameEventField, `levels.${typeof index}.puzzles`>({
    name: `levels.${index}.puzzles`,
    control,
  });

  const onAddNewPuzzle = useCallback(
    (puzzle) => {
      append({
        puzzleId: puzzle.puzzleId,
        icon: puzzle.icon,
        name: puzzle.name,
      });
      trigger(`levels.${index}.puzzles`);
    },
    [append],
  );

  const difficultiesOptions = useMemo(() => {
    if (!gameplay) {
      return [];
    }

    return Object.keys(gameplay.difficulties_actual_and_display).map((difficulty) => ({
      label: difficulty,
      value: parseInt(difficulty),
    }));
  }, [gameplay]);

  const errors = formState.errors.levels?.[index] || {};
  const levelData = data?.levels?.[index] || {};

  const { message: puzzleErrorMessage } = (errors as any)['puzzles'] || {};
  return (
    <FlexContainer flexDirection="column">
      <FlexContainer flexDirection="row" justifyContent="space-between" margin="0 0 20px 0">
        <FlexContainer>
          <Text size={1} weight="bold">
            {strings.gameEvent.form.level.level} {level}
          </Text>
        </FlexContainer>
        <FlexContainer>
          <Input
            required
            placeholder={strings.gameEvent.form.placeholder.levelReward}
            name={`levels.${index}.reward`}
            control={control}
            error={errors['reward']}
            type="number"
            width="400px"
            disabled={disabled}
            defaultValue={levelData?.reward}
          />
        </FlexContainer>
        <FlexContainer>
          <SelectForm
            required
            placeholder={strings.gameEvent.form.placeholder.levelDifficulty}
            name={`levels.${index}.difficulty`}
            control={control}
            disabled={disabled}
            values={difficultiesOptions}
            error={errors['difficulty']}
            defaultValue={levelData?.difficulty}
            width="400px"
          />
        </FlexContainer>
      </FlexContainer>
      <PuzzleListContainer flexDirection="row" margin="10px">
        {puzzles.map((value, index) => (
          <PuzzleItem
            key={value.puzzleId}
            id={value.puzzleId}
            onRemove={() => remove(index)}
            disabled={disabled}
          />
        ))}

        <AddNewItem onAddItem={onAddNewPuzzle} disabled={disabled} />

        {puzzleErrorMessage && <ErrorMessage>{puzzleErrorMessage}</ErrorMessage>}
      </PuzzleListContainer>
      {(isLast || puzzles.length === 0) && (
        <FlexContainer margin="20px auto 20px auto">
          <Button
            style={{ minWidth: 150 }}
            variant="danger"
            height={30}
            disabled={disabled}
            onClick={() => onRemove(index)}
          >
            {strings.gameEvent.form.buttons.remove}
          </Button>
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

export default GameEventLevelForm;
